// Extracted verbatim from the v3 monolith (see BIRDSEYE.md module split plan).

import { DATA_PAGE } from './constants.js';
import { toArray } from './utils.js';

export var _setupPaginationTemplates = function () {
  var numberTemplate = this.wrapper.querySelector(DATA_PAGE + '[data-pf-page="number"]');
  var dotsTemplate = this.wrapper.querySelector(DATA_PAGE + '[data-pf-page="dots"]');

  if (!numberTemplate) {
    var container = this.wrapper.querySelector('[data-pf-pages]');
    if (!container) {
      container = document.createElement('div');
      container.setAttribute('data-pf-pages', '');
      this.wrapper.appendChild(container);
    }

    this.pageButtons.numbersContainer = container;
    this.pageButtons.numberTemplate = null;
    this.pageButtons.dotsTemplate = dotsTemplate ? dotsTemplate.cloneNode(true) : null;
    if (dotsTemplate) {
      dotsTemplate.remove();
    }
    return;
  }

  this.pageButtons.numbersContainer = numberTemplate.parentElement;
  this.pageButtons.numberTemplate = numberTemplate.cloneNode(true);
  this.pageButtons.dotsTemplate = dotsTemplate ? dotsTemplate.cloneNode(true) : null;
  numberTemplate.remove();
  if (dotsTemplate) {
    dotsTemplate.remove();
  }

  if (this.pageButtons.numbersContainer && !this.pageButtons.numbersContainer.hasAttribute('role')) {
    this.pageButtons.numbersContainer.setAttribute('role', 'navigation');
    this.pageButtons.numbersContainer.setAttribute('aria-label', 'Pagination');
  }
};

export var _setupInfiniteScroll = function () {
  var self = this;

  if (this.paginationMode !== 'infinite') {
    return;
  }

  if (!window.IntersectionObserver) {
    this.paginationMode = 'loadmore';
    this._debugLog('infinite:fallback:loadmore');
    return;
  }

  var sentinel = this.wrapper.querySelector('[data-pf-infinite-sentinel]');
  if (!sentinel) {
    sentinel = document.createElement('div');
    sentinel.setAttribute('data-pf-infinite-sentinel', '');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.width = '100%';
    sentinel.style.height = '1px';
    if (this.list && this.list.parentNode) {
      this.list.parentNode.insertBefore(sentinel, this.list.nextSibling);
    } else {
      this.wrapper.appendChild(sentinel);
    }
    this._ownsInfiniteSentinel = true;
  }
  this.infiniteSentinel = sentinel;

  if (this.infiniteObserver) {
    this.infiniteObserver.disconnect();
  }

  this.infiniteObserver = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) {
          return;
        }
        self._advanceInfinite();
      });
    },
    {
      root: null,
      rootMargin: '0px 0px 20% 0px',
      threshold: this.infiniteThreshold,
    }
  );

  this.infiniteObserver.observe(sentinel);
  this._debugLog('infinite:init', {
    threshold: this.infiniteThreshold,
  });
};

export var _refreshInfiniteState = function () {
  if (this.paginationMode !== 'infinite' || !this.infiniteSentinel) {
    return;
  }

  var hasMore =
    this.currentPage < this.totalPages ||
    ((this.loadMode === 'auto' || this.loadMode === 'all') &&
      this.pendingPageUrls.length > 0);

  this.infiniteSentinel.hidden = !hasMore;
};

export var _advanceInfinite = function () {
  var self = this;

  if (this.paginationMode !== 'infinite' || this.infiniteBusy) {
    return Promise.resolve();
  }

  this.infiniteBusy = true;
  this._debugLog('infinite:advance:start', {
    page: this.currentPage,
    totalPages: this.totalPages,
    pendingPages: this.pendingPageUrls.length,
  });

  var task = Promise.resolve();
  if (this.currentPage < this.totalPages) {
    this.goToPage(this.currentPage + 1);
  } else if (
    (this.loadMode === 'auto' || this.loadMode === 'all') &&
    this.pendingPageUrls.length
  ) {
    task = this.ensureEnoughItemsForCurrentView().then(function () {
      if (self.currentPage < self.totalPages) {
        self.goToPage(self.currentPage + 1);
      }
    });
  }

  return task.finally(function () {
    self.infiniteBusy = false;
    self._refreshInfiniteState();
    self._debugLog('infinite:advance:end', {
      page: self.currentPage,
      totalPages: self.totalPages,
      pendingPages: self.pendingPageUrls.length,
    });
  });
};

export var _paginate = function () {
  var total = this.filteredItems.length;

  if (this.paginationMode === 'all') {
    this.totalPages = total > 0 ? 1 : 0;
    this.currentPage = 1;
    return this.filteredItems.slice();
  }

  this.totalPages = total > 0 ? Math.ceil(total / this.pageSize) : 0;

  if (this.totalPages === 0) {
    this.currentPage = 1;
    return [];
  }

  if (this.currentPage > this.totalPages) {
    this.currentPage = this.totalPages;
  }

  if (this.currentPage < 1) {
    this.currentPage = 1;
  }

  if (this.paginationMode === 'loadmore' || this.paginationMode === 'infinite') {
    var end = this.currentPage * this.pageSize;
    return this.filteredItems.slice(0, end);
  }

  var start = (this.currentPage - 1) * this.pageSize;
  var finish = start + this.pageSize;
  return this.filteredItems.slice(start, finish);
};

export var _setPageControlState = function (button, options) {
  if (!button) {
    return;
  }

  var cfg = options || {};
  var modeVisible = cfg.modeVisible !== false;
  var available = !!cfg.available;
  var hide = !modeVisible || (this.pageHideUnavailable && !available);
  var tag = (button.tagName || '').toLowerCase();

  button.hidden = hide;
  button.style.display = hide ? 'none' : '';
  button.setAttribute('aria-hidden', hide ? 'true' : 'false');
  button.setAttribute('aria-disabled', available ? 'false' : 'true');
  button.setAttribute('data-pf-disabled', available ? 'false' : 'true');
  button.setAttribute('data-pf-state', available ? 'active' : 'inactive');

  if ('disabled' in button) {
    button.disabled = !available;
  } else if (tag === 'a') {
    button.setAttribute('tabindex', available ? '0' : '-1');
  }

  if (this.pageDisabledClass) {
    button.classList.toggle(this.pageDisabledClass, !available);
  }
  if (this.pageHiddenClass) {
    button.classList.toggle(this.pageHiddenClass, hide);
  }
};

export var _renderPagination = function () {
  var hasPages = this.totalPages > 1;
  var hasAnyPage = this.totalPages > 0;
  var isEmpty = this.filteredItems.length === 0;
  var isNumbersMode = this.paginationMode === 'numbers';
  var isLoadMoreMode = this.paginationMode === 'loadmore';
  var isInfiniteMode = this.paginationMode === 'infinite';
  var isAllMode = this.paginationMode === 'all';

  this.pageButtons.prev.forEach(
    function (btn) {
      this._setPageControlState(btn, {
        modeVisible: isNumbersMode && !isEmpty,
        available: isNumbersMode && hasAnyPage && this.currentPage > 1,
      });
    }.bind(this)
  );

  this.pageButtons.next.forEach(
    function (btn) {
      var modeVisible = isNumbersMode || isLoadMoreMode;
      var available = modeVisible && hasAnyPage && this.currentPage < this.totalPages;
      if (isLoadMoreMode) {
        btn.setAttribute('aria-label', 'Load more');
      }

      this._setPageControlState(btn, {
        modeVisible: modeVisible && !isInfiniteMode && !isAllMode && !isEmpty,
        available: available,
      });
    }.bind(this)
  );

  if (!this.pageButtons.numbersContainer) {
    return;
  }

  this.pageButtons.numbersContainer.hidden = !hasPages || !isNumbersMode || isEmpty;

  if (!hasPages || !isNumbersMode || isEmpty) {
    this.pageButtons.numbersContainer.innerHTML = '';
    return;
  }

  var fragment = document.createDocumentFragment();
  var tokens = this._buildPaginationTokens();
  var self = this;

  tokens.forEach(function (token) {
    if (token === null) {
      fragment.appendChild(self._createDotsNode());
      return;
    }

    var button = self.pageButtons.numberTemplate
      ? self.pageButtons.numberTemplate.cloneNode(true)
      : document.createElement('button');
    button.setAttribute('data-pf-page', 'number');
    button.setAttribute('data-pf-page-number', String(token));
    button.textContent = String(token);
    button.hidden = false;

    if (!button.hasAttribute('type')) {
      button.setAttribute('type', 'button');
    }

    if (token === self.currentPage) {
      button.setAttribute('aria-current', 'page');
    } else {
      button.removeAttribute('aria-current');
    }

    fragment.appendChild(button);
  });

  this.pageButtons.numbersContainer.innerHTML = '';
  this.pageButtons.numbersContainer.appendChild(fragment);
};

export var _createDotsNode = function () {
  var dots = this.pageButtons.dotsTemplate
    ? this.pageButtons.dotsTemplate.cloneNode(true)
    : document.createElement('span');

  dots.setAttribute('data-pf-page', 'dots');
  dots.textContent = this.pageDotsLabel;
  dots.setAttribute('aria-hidden', 'true');
  return dots;
};

export var _buildPaginationTokens = function () {
  var total = this.totalPages;
  var current = this.currentPage;
  var siblings = this.pageSiblings;
  var boundary = this.pageBoundary;

  if (total <= 0) {
    return [];
  }

  var pagesSet = new Set();
  var page;

  for (page = 1; page <= boundary; page += 1) {
    if (page <= total) {
      pagesSet.add(page);
    }
  }

  for (page = total - boundary + 1; page <= total; page += 1) {
    if (page >= 1) {
      pagesSet.add(page);
    }
  }

  for (page = current - siblings; page <= current + siblings; page += 1) {
    if (page >= 1 && page <= total) {
      pagesSet.add(page);
    }
  }

  var pages = toArray(pagesSet).sort(function (a, b) {
    return a - b;
  });

  var tokens = [];
  var previous = null;

  pages.forEach(function (value) {
    if (previous !== null && value - previous > 1) {
      tokens.push(null);
    }
    tokens.push(value);
    previous = value;
  });

  return tokens;
};

